import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@clerk/react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { makeApi } from "@/lib/api";
import Shell from "@/components/Shell";
import FlowSteps from "@/components/FlowSteps";
import ComplianceProgress from "@/components/ComplianceProgress";
import ReferralCard from "@/components/ReferralCard";
import Boc3Alert from "@/components/Boc3Alert";
import NewEntrantAudit from "@/components/NewEntrantAudit";
import { useLang } from "@/contexts/LanguageContext";
import { filingGuidance, overviewGuidance, STATUS_META } from "@/lib/guidance";
import { expiryAlerts } from "@/lib/compliance";
import { ExpiryAlerts } from "@/pages/Compliance";
import { ArrowRight, CheckCircle2, Circle, Clock, FileText, Sparkles, ExternalLink, Printer, BookOpen, ChevronLeft, ChevronRight, AlertTriangle, Compass, Undo2, RotateCcw } from "lucide-react";

const FMCSA = "https://www.fmcsa.dot.gov/registration";
const FORMS = `${FMCSA}/registration-forms`;
const filingLinks = {
  usdot: { online: `${FMCSA}/getting-started`, forms: FORMS },
  mc_authority: { online: `${FMCSA}/get-mc-number-authority-operate`, forms: FORMS },
  boc3: {
    online: `${FMCSA}/form-boc-3-designation-agents-service-process`,
    onlineLabel: "View BOC-3",
    forms: `${FMCSA}/process-agents`,
    formsLabel: "Process agents",
    formsIcon: "web",
  },
  bmc84: { online: `${FMCSA}/insurance-requirements`, forms: FORMS },
  bmc91: { online: `${FMCSA}/insurance-requirements`, forms: FORMS },
  ucr: { online: "https://plan.ucr.gov/" },
  irp_ifta: { online: "https://www.irponline.org/", forms: "https://www.iftach.org/", formsLabel: "IFTA", onlineLabel: "IRP" },
  insurance: { online: `${FMCSA}/insurance-requirements` },
  eld_enroll: { online: "https://eld.fmcsa.dot.gov/List" },
  drug_testing: {
    online: "https://www.fmcsa.dot.gov/regulations/drug-alcohol-testing-program",
    onlineLabel: "Testing rules",
    forms: "https://clearinghouse.fmcsa.dot.gov/",
    formsLabel: "Clearinghouse",
    formsIcon: "web",
  },
  safety_inspection: {
    online: "https://www.fmcsa.dot.gov/safety/driver-safety/commercial-driver-medical-requirements",
    onlineLabel: "Inspection",
    forms: "https://www.fmcsa.dot.gov/safety/safety-programs/compliance-safety-accountability",
    formsLabel: "Prep Guide",
    formsIcon: "guide",
  },
};

const linkCls = "min-h-[44px] text-[11px] font-mono-label border border-[#262626] hover:border-[#0055FF] hover:text-white px-2 py-2 inline-flex items-center justify-center gap-1 transition-colors";

function GuidancePanel({ sections }) {
  if (!sections.length) return null;
  return (
    <div className="brutal-border bg-[#141414] mb-10" data-testid="guidance-panel">
      <div className="px-5 py-4 border-b border-[#262626] flex items-center gap-2">
        <Compass size={16} className="text-[#FFD600]" />
        <h2 className="font-heading font-black text-lg">Your tailored guidance</h2>
      </div>
      <div className="grid md:grid-cols-3">
        {sections.map((s) => (
          <div key={s.key} className="p-5 border-b md:border-b-0 md:border-r border-[#262626] last:border-0" data-testid={`guidance-${s.key}`}>
            <div className="font-mono-label text-[11px] text-[#FFD600] mb-3">{s.title}</div>
            {(s.warnings || []).map((w, i) => (
              <p key={`w${i}`} className="text-sm mb-2 flex gap-2 text-[#FFB020]" data-testid={`guidance-warning-${s.key}-${i}`}>
                <AlertTriangle size={15} className="shrink-0 mt-0.5" /> <span>{w}</span>
              </p>
            ))}
            <ul className="space-y-2">
              {s.notes.map((n, i) => <li key={i} className="text-sm text-muted-foreground leading-relaxed">{n}</li>)}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

function FilingLinks({ fkey }) {
  const l = filingLinks[fkey];
  if (!l) return null;
  return (
    <div className="grid grid-cols-2 gap-2">
      <a href={l.online} target="_blank" rel="noopener noreferrer" data-testid={`fmcsa-online-${fkey}`} className={`${linkCls} ${l.forms ? "" : "col-span-2"}`}>
        <ExternalLink size={11} /> {l.onlineLabel || "Online"}
      </a>
      {l.forms && (
        <a href={l.forms} target="_blank" rel="noopener noreferrer" data-testid={`fmcsa-forms-${fkey}`} className={linkCls}>
          {l.formsIcon === "guide" ? <BookOpen size={11} /> : l.formsIcon === "web" ? <ExternalLink size={11} /> : <Printer size={11} />} {l.formsLabel || "Print forms"}
        </a>
      )}
    </div>
  );
}

const statusMeta = {
  not_started: { label: "Not started", icon: Circle, color: "text-muted-foreground", pct: 0 },
  in_progress: { label: "In progress", icon: Clock, color: "text-[#FFD600]", pct: 50 },
  submitted: { label: "Submitted", icon: Clock, color: "text-[#0055FF]", pct: 80 },
  completed: { label: "Completed", icon: CheckCircle2, color: "text-emerald-400", pct: 100 },
};
const nextStatus = { not_started: "in_progress", in_progress: "submitted", submitted: "completed", completed: "completed" };
const prevStatus = { in_progress: "not_started", submitted: "in_progress", completed: "submitted" };

export default function Dashboard() {
  const { getToken } = useAuth();
  const api = useMemo(() => makeApi(getToken), [getToken]);
  const { t } = useLang();
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();

  const goBack = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate("/wizard");
  };

  const saveContinue = () => {
    toast.success("Progress saved");
    navigate("/compliance");
  };

  const [records, setRecords] = useState([]);
  const load = async () => {
    const [{ data }, c] = await Promise.all([api.get("/profile"), api.get("/compliance")]);
    setProfile(data);
    setRecords(c.data.items);
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (f, ns) => {
    await api.put(`/filings/${f.key}`, { status: ns, value: f.value });
    if (f.key === "drug_testing") window.dispatchEvent(new Event("drug-testing-changed"));
    load();
  };
  const advance = (f) => setStatus(f, nextStatus[f.status]);

  if (!profile) return <Shell><div className="font-mono-label text-xs text-muted-foreground">LOADING…</div></Shell>;

  const filings = profile.filings || [];
  const completed = filings.filter((f) => f.status === "completed").length;
  const totalPct = Math.round(filings.reduce((a, f) => a + statusMeta[f.status].pct, 0) / Math.max(filings.length, 1));
  const guide = filingGuidance(profile);
  const overview = overviewGuidance(profile);

  return (
    <Shell>
      <FlowSteps current={0} />
      <div className="mb-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <div className="font-mono-label text-xs text-[#FFD600] mb-2">CARRIER OPERATIONS CENTER</div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl leading-none break-words" data-testid="dashboard-title">
            {profile.legal_name || "Your Carrier"}.
          </h1>
          <div className="text-sm text-muted-foreground mt-2">
            {profile.operation_type || "Interstate"} · {profile.home_state || "—"} · {profile.vehicles?.length || 0} vehicle types
          </div>
        </div>
        <div className="flex gap-3">
          <Link to="/wizard" data-testid="dashboard-edit-profile" className="min-h-[44px] px-4 py-2 border border-[#262626] hover:border-white font-mono-label text-xs inline-flex items-center transition-colors">
            Edit Setup
          </Link>
          <Link to="/chat" data-testid="dashboard-ask-ai" className="min-h-[44px] cta-primary px-4 py-2 font-mono-label text-xs inline-flex items-center gap-2">
            <Sparkles size={14} /> Ask AI
          </Link>
        </div>
      </div>

      <Boc3Alert profile={profile} api={api} onChanged={load} guides={guide} />
      <ComplianceProgress profile={profile} records={records} />
      <ReferralCard api={api} />

      {/* KPI row */}
      <div className="brutal-border grid grid-cols-2 md:grid-cols-4 mb-10">
        <KPI label={t("filings") + " / Total"} value={`${completed}/${filings.length}`} testid="kpi-filings" />
        <KPI label="Filing Progress" value={`${totalPct}%`} accent testid="kpi-progress" />
        <KPI label="Operation" value={profile.operation_type || "—"} testid="kpi-operation" />
        <KPI label="ELD" value={profile.eld_company || "—"} testid="kpi-eld" />
      </div>

      <ExpiryAlerts alerts={expiryAlerts(profile, records)} />
      <GuidancePanel sections={overview} />

      {/* Filings grid */}
      <h2 id="filings" className="font-heading font-black text-2xl mb-4 scroll-mt-24" data-testid="filings-header">{t("filings")}</h2>
      <div className="brutal-border grid md:grid-cols-2 lg:grid-cols-3">
        {filings.map((f) => {
          const sm = statusMeta[f.status];
          const Icon = sm.icon;
          const gd = guide[f.key] || { status: "check", notes: [] };
          const badge = STATUS_META[gd.status];
          return (
            <div key={f.key} data-testid={`filing-card-${f.key}`} className={`p-5 border-r border-b border-[#262626] bg-[#0A0A0A] flex flex-col gap-3 ${gd.status === "not_required" ? "opacity-70" : ""}`}>
              <div className="flex items-start justify-between gap-2">
                <span className={`font-mono-label text-[10px] px-2 py-1 ${badge.cls}`} data-testid={`filing-need-${f.key}`}>{badge.label}</span>
                <Icon size={16} className={sm.color} />
              </div>
              <div>
                <div className="font-mono-label text-[10px] text-muted-foreground mb-1 flex items-center gap-1.5"><FileText size={12} /> {f.key.toUpperCase()}</div>
                <div className="font-heading font-bold text-base leading-tight">{f.label}</div>
              </div>
              <div className={`text-xs font-mono-label ${sm.color}`}>{sm.label}</div>
              {gd.notes.length > 0 && (
                <ul className="space-y-1.5 text-[13px] text-muted-foreground leading-relaxed list-disc pl-4 marker:text-[#FFD600]" data-testid={`filing-notes-${f.key}`}>
                  {gd.notes.map((n, i) => <li key={i}>{n}</li>)}
                </ul>
              )}
              {f.key === "safety_inspection" && <NewEntrantAudit profile={profile} api={api} onChanged={load} />}
              <div className="mt-auto"><FilingLinks fkey={f.key} /></div>
              <button
                onClick={() => advance(f)}
                disabled={f.status === "completed"}
                data-testid={`advance-${f.key}`}
                className="min-h-[44px] text-xs font-mono-label border border-[#262626] hover:border-white disabled:opacity-30 px-3 py-2 inline-flex items-center justify-between transition-colors"
              >
                {f.status === "completed" ? "Done" : "Advance"} <ArrowRight size={12} />
              </button>
              {f.status !== "not_started" && (
                <div className="grid grid-cols-2 gap-2 -mt-2" data-testid={`status-controls-${f.key}`}>
                  <button onClick={() => setStatus(f, prevStatus[f.status])} data-testid={`stepback-${f.key}`}
                    className="min-h-[40px] text-[11px] font-mono-label border border-[#262626] hover:border-white text-muted-foreground hover:text-white px-2 inline-flex items-center justify-center gap-1.5 transition-colors">
                    <Undo2 size={12} /> Step back
                  </button>
                  <button onClick={() => setStatus(f, "not_started")} data-testid={`clear-${f.key}`}
                    className="min-h-[40px] text-[11px] font-mono-label border border-[#262626] hover:border-[#DC2626] text-muted-foreground hover:text-[#F87171] px-2 inline-flex items-center justify-center gap-1.5 transition-colors">
                    <RotateCcw size={12} /> Clear
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center justify-between mt-12 gap-4">
        <button
          onClick={goBack}
          data-testid="filings-back-button"
          className="px-5 py-3 border border-[#262626] hover:border-white font-mono-label text-xs inline-flex items-center gap-2 transition-colors"
        >
          <ChevronLeft size={14} /> {t("back")}
        </button>
        <button
          onClick={saveContinue}
          data-testid="filings-save-continue-button"
          className="cta-primary px-6 py-3 font-mono-label text-xs inline-flex items-center gap-2"
        >
          {t("saveContinue")} <ChevronRight size={14} />
        </button>
      </div>
    </Shell>
  );
}

function KPI({ label, value, accent, testid }) {
  return (
    <div className={`p-4 sm:p-6 border-r border-b border-[#262626] min-w-0 ${accent ? "bg-[#FFD600] text-[#0A0A0A]" : "bg-[#141414]"}`} data-testid={testid}>
      <div className={`font-mono-label text-[10px] mb-3 ${accent ? "text-[#0A0A0A]/70" : "text-muted-foreground"}`}>{label}</div>
      <div className="font-heading font-black text-xl sm:text-3xl leading-tight break-words">{value}</div>
    </div>
  );
}
